<template>
  <b-form-input
    type="text"
    inputmode="decimal"
    autocomplete="off"
    v-bind="$attrs"
    :value="display"
    v-on="forwardedListeners"
    @keydown="onKeydown"
    @input="onInput"
    @blur="onBlur"
    @focus="onFocus"
    @click="onClick"
  />
</template>

<script>
/**
 * Input de monto con formato es-AR en vivo mientras se tipea (miles con
 * punto, decimales con coma) — mismo v-model de siempre (número crudo), no
 * un string formateado. Reemplazo de `<b-form-input type="number">` en
 * inputs de montos EDITABLES (los de solo lectura ya se resuelven con un
 * computed + Intl.NumberFormat, ver docs/list-view-interfaces).
 *
 * Estrategia: "máscara de cajero" — cada tecla se trata como el próximo
 * dígito de la derecha (centavos), nunca se inserta el separador en una
 * posición intermedia del string tipeado. Evita todo el problema de
 * mantener la posición del cursor mientras se reformatea en cada tecla
 * (que sí hay que resolver si se intenta el approach "parsear tal cual lo
 * tipeó el usuario") — con dígitos siempre agregándose al final, el cursor
 * queda naturalmente al final también. Es el mismo patrón que un
 * datáfono/POS: escribís "1234" y ves "12,34".
 */
export default {
  name: 'AppNumberInput',
  inheritAttrs: false,
  props: {
    value: { type: [Number, String], default: 0 },
    decimals: { type: Number, default: 2 },
    selectOnFocus: { type: Boolean, default: true },
  },
  data() {
    return {
      cents: this.toCents(this.value),
      lastEmitted: this.toNumber(this.value),
      decimalMode: false,
      decimalDigits: '',
      integerDigits: '',
      cleared: false,
    }
  },
  computed: {
    display() {
      if (this.cleared) {
        return ''
      }
      if (this.decimalMode) {
        return this.formatDecimalEditing()
      }
      return this.formatCents(this.cents)
    },
    // @input lo maneja este componente (necesita interceptarlo para
    // aplicar la máscara) — el resto de los listeners del padre (@blur,
    // @change, @keydown, etc.) se reenvían tal cual.
    forwardedListeners() {
      // eslint-disable-next-line no-unused-vars
      const { input, keydown, blur, focus, click, ...rest } = this.$listeners
      return rest
    },
  },
  watch: {
    value(newValue) {
      const num = this.toNumber(newValue)
      // Si el cambio vino de nuestro propio $emit, no hay que resincronizar
      // (cortaría el tipeo del usuario a mitad de camino).
      if (num === this.lastEmitted) return
      this.cents = this.toCents(newValue)
      this.decimalMode = false
      this.decimalDigits = ''
      this.integerDigits = ''
      this.cleared = false
    },
  },
  methods: {
    toNumber(val) {
      return parseFloat(val) || 0
    },
    toCents(val) {
      return Math.round(this.toNumber(val) * Math.pow(10, this.decimals))
    },
    formatCents(cents) {
      const num = cents / Math.pow(10, this.decimals)
      return new Intl.NumberFormat('es-AR', {
        minimumFractionDigits: this.decimals,
        maximumFractionDigits: this.decimals,
      }).format(num)
    },
    formatInteger(value) {
      return new Intl.NumberFormat('es-AR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value)
    },
    formatDecimalEditing() {
      const factor = Math.pow(10, this.decimals)
      const integer = Math.trunc(this.cents / factor)
      return this.formatInteger(integer) + ',' + this.decimalDigits
    },
    emitNumber(num) {
      this.cents = this.toCents(num)
      this.lastEmitted = num
      this.$emit('input', num)
    },
    callListener(name, event) {
      const listener = this.$listeners[name]
      if(Array.isArray(listener)) {
        listener.forEach(fn => fn(event))
      } else if(typeof listener === 'function') {
        listener(event)
      }
    },
    setCursorEnd() {
      this.$nextTick(() => {
        const el = this.$el
        if (el && el.setSelectionRange) {
          el.value = this.display
          const len = el.value.length
          el.setSelectionRange(len, len)
        }
      })
    },
    hasFullSelection(event) {
      const el = event.target
      return el && el.selectionStart === 0 && el.selectionEnd === el.value.length && el.value.length > 0
    },
    isDecimalSeparator(key) {
      return key === ',' || key === '.'
    },
    clearInput() {
      this.cents = 0
      this.lastEmitted = 0
      this.decimalMode = false
      this.decimalDigits = ''
      this.integerDigits = ''
      this.cleared = true
      this.$emit('input', 0)
      this.$nextTick(() => {
        if(this.$el) {
          this.$el.value = ''
        }
      })
    },
    startDecimalInput() {
      this.cents = 0
      this.decimalMode = true
      this.decimalDigits = ''
      this.integerDigits = ''
      this.cleared = false
      this.updateDecimalValue()
    },
    updateDecimalValue() {
      const factor = Math.pow(10, this.decimals)
      const integer = Math.trunc(this.cents / factor)
      const decimalText = (this.decimalDigits + '0'.repeat(this.decimals)).slice(0, this.decimals)
      const num = integer + ((parseInt(decimalText, 10) || 0) / factor)
      this.emitNumber(num)
      this.setCursorEnd()
    },
    onKeydown(event) {
      this.callListener('keydown', event)

      if(event.defaultPrevented) {
        return
      }

      if(this.hasFullSelection(event)) {
        if(event.key === 'Backspace' || event.key === 'Delete') {
          event.preventDefault()
          this.clearInput()
          return
        }

        if(/^[0-9]$/.test(event.key)) {
          event.preventDefault()
          this.clearInput()
          this.onInput(event.key)
          return
        }

        if(this.isDecimalSeparator(event.key) && this.decimals > 0) {
          event.preventDefault()
          this.startDecimalInput()
          return
        }
      }

      if(this.isDecimalSeparator(event.key) && this.decimals > 0) {
        event.preventDefault()
        const factor = Math.pow(10, this.decimals)
        const integer = this.integerDigits ? parseInt(this.integerDigits, 10) : Math.trunc(this.cents / factor)
        this.cents = integer * factor
        this.decimalMode = true
        this.decimalDigits = ''
        this.updateDecimalValue()
        return
      }

      if(!this.decimalMode) {
        return
      }

      if(/^[0-9]$/.test(event.key)) {
        event.preventDefault()
        if(this.decimalDigits.length < this.decimals) {
          this.decimalDigits += event.key
          this.updateDecimalValue()
        }
        return
      }

      if(event.key === 'Backspace') {
        event.preventDefault()
        if(this.decimalDigits.length) {
          this.decimalDigits = this.decimalDigits.slice(0, -1)
          this.updateDecimalValue()
        } else {
          this.decimalMode = false
          this.setCursorEnd()
        }
        return
      }

      if(event.key === 'Delete') {
        event.preventDefault()
      }
    },
    onInput(text) {
      if(!text) {
        this.clearInput()
        return
      }

      this.cleared = false

      if(this.decimalMode) {
        this.setCursorEnd()
        return
      }

      const digits = (text || '').replace(/[^0-9]/g, '')
      this.integerDigits = digits
      this.emitNumber(digits ? parseInt(digits, 10) / Math.pow(10, this.decimals) : 0)
      // El string se reemplaza entero en cada tecla (separadores de miles
      // que se corren de posición) — sin esto el cursor podía terminar en
      // cualquier lado en vez de al final, que es donde entra el próximo
      // dígito con esta máscara.
      this.setCursorEnd()
    },
    onBlur(event) {
      this.decimalMode = false
      this.decimalDigits = ''
      this.integerDigits = ''
      this.cleared = false
      if(!this.$el.value) {
        this.emitNumber(0)
        this.setCursorEnd()
      }
      this.callListener('blur', event)
    },
    onFocus(event) {
      this.callListener('focus', event)
      this.selectText(event.target)
    },
    onClick(event) {
      this.callListener('click', event)
      // El click puede reposicionar el cursor después de focus.
      this.$nextTick(() => this.selectText(event.target))
    },
    selectText(input) {
      if(!this.selectOnFocus || !input || typeof input.select !== 'function') {
        return
      }
      input.select()
    },
  },
}
</script>
